import { destinationById, type PostDestinationId } from './destinations.js';
import type { PostPlatform } from '../../types/publishing.js';

// Publishing — multi-account posting (2026-07-28). Compose ONCE (one media set +
// one caption with wildcards + one set of destination ticks) and fan out to many
// accounts, the caption resolving per account. Structurally the broadcast module
// applied to posts: audience selection → shared content → per-target resolution →
// resilient fan-out.
//
// This file is the PURE core shared by the coverage preview (client) and the
// fan-out planner (server), so "which account posts to which destination, and
// which receive nothing" is computed one way and unit-tested once.
//
// THE COVERAGE RULE (locked with Scott): a ticked destination is applied to an
// account only where that account has the destination's PLATFORM connected. A
// destination the account lacks is SHOWN in the preview + SKIPPED for that account
// (not a hard failure); an account with zero covered destinations receives nothing
// and is not created.

export type PlatformConnectivity = Record<PostPlatform, boolean>;

// A destination is covered for an account iff its platform is connected — feed and
// story on the same platform both hinge on that one platform connection.
export function isDestinationCovered(
  connected: PlatformConnectivity,
  dest: PostDestinationId,
): boolean {
  return connected[destinationById(dest).platform] === true;
}

// ---------------------------------------------------------------------------
// Coverage preview (client) — destination-level, for the ChannelIcon glyphs.
// ---------------------------------------------------------------------------

export interface CoverageAccount {
  id: string;
  name: string;
  connected: PlatformConnectivity;
  brandName?: string | null;
}

export interface AccountCoverage {
  account: CoverageAccount;
  /** Ticked destinations the account WILL post to (platform connected). */
  covered: PostDestinationId[];
  /** Ticked destinations skipped for this account (platform not connected). */
  skipped: PostDestinationId[];
  /** False ⇒ none of the ticked destinations are connected — receives nothing. */
  hasCoverage: boolean;
}

export function coverageForAccount(
  account: CoverageAccount,
  ticked: PostDestinationId[],
): AccountCoverage {
  const covered: PostDestinationId[] = [];
  const skipped: PostDestinationId[] = [];
  for (const dest of ticked) {
    (isDestinationCovered(account.connected, dest) ? covered : skipped).push(dest);
  }
  return { account, covered, skipped, hasCoverage: covered.length > 0 };
}

export function buildCoveragePlan(
  accounts: CoverageAccount[],
  ticked: PostDestinationId[],
): AccountCoverage[] {
  return accounts.map((a) => coverageForAccount(a, ticked));
}

export interface CoverageSummary {
  totalSelected: number;
  /** Accounts that will receive at least one destination. */
  willReceive: number;
  /** Accounts that would receive nothing (flagged, not created). */
  zeroCoverage: number;
}

export function summarizeCoverage(plan: AccountCoverage[]): CoverageSummary {
  const willReceive = plan.filter((p) => p.hasCoverage).length;
  return {
    totalSelected: plan.length,
    willReceive,
    zeroCoverage: plan.length - willReceive,
  };
}

// ---------------------------------------------------------------------------
// Fan-out plan (server) — target-level, generic over the target shape so the
// server passes its own PostTargetInput[]. Keeps each account's KEPT targets
// (platform connected) and flags the coverage-zero accounts, which are NOT
// created. Same underlying platform-connectivity rule as the coverage preview.
// ---------------------------------------------------------------------------

export interface FanOutTarget {
  platform: PostPlatform;
}

export interface FanOutAccount {
  id: string;
  connected: PlatformConnectivity;
}

export interface FanOutAccountPlan<T extends FanOutTarget> {
  accountId: string;
  /** The targets this account will actually be dispatched (platform connected). */
  targets: T[];
  /** False ⇒ no ticked target is connected — skip (do not create a post). */
  hasCoverage: boolean;
}

export function planPostFanOut<T extends FanOutTarget>(
  accounts: FanOutAccount[],
  targets: T[],
): FanOutAccountPlan<T>[] {
  return accounts.map((account) => {
    const kept = targets.filter((t) => account.connected[t.platform] === true);
    return { accountId: account.id, targets: kept, hasCoverage: kept.length > 0 };
  });
}

// ---------------------------------------------------------------------------
// Resilient fan-out orchestration (server) — pure over an injected per-account
// runner, so the "one account failing never aborts the batch" resilience (the
// broadcast lesson) is unit-testable without Firestore / Zernio.
// ---------------------------------------------------------------------------

export type FanOutStatus = 'created' | 'skipped' | 'failed';

export interface FanOutResultRow {
  accountId: string;
  accountName: string | null;
  status: FanOutStatus;
  postId: string | null;
  // A dispatch failure that still SAVED the post as a draft (status 'created') —
  // the per-account resilience outcome, surfaced but not fatal.
  dispatchError: string | null;
  // Why an account was skipped (coverage-zero) or failed (hard error, no post).
  reason: string | null;
}

// What the per-account runner reports. A THROWN error is caught by runFanOut and
// recorded as 'failed' (so the runner can throw freely on a hard error).
export type FanOutAttempt =
  | { status: 'created'; accountName: string | null; postId: string; dispatchError: string | null }
  | { status: 'skipped'; accountName: string | null; reason: string }
  | { status: 'failed'; accountName: string | null; reason: string };

export interface FanOutSummary {
  results: FanOutResultRow[];
  created: number;
  skipped: number;
  failed: number;
}

// Run `runOne` for each (deduped) account, tolerating a per-account failure: a
// runner that throws is recorded 'failed' and the loop CONTINUES — never
// all-or-nothing. Results preserve input order.
export async function runFanOut(
  accountIds: string[],
  runOne: (accountId: string) => Promise<FanOutAttempt>,
): Promise<FanOutSummary> {
  const results: FanOutResultRow[] = [];
  for (const accountId of [...new Set(accountIds)]) {
    try {
      const a = await runOne(accountId);
      if (a.status === 'created') {
        results.push({ accountId, accountName: a.accountName, status: 'created', postId: a.postId, dispatchError: a.dispatchError, reason: null });
      } else if (a.status === 'skipped') {
        results.push({ accountId, accountName: a.accountName, status: 'skipped', postId: null, dispatchError: null, reason: a.reason });
      } else {
        results.push({ accountId, accountName: a.accountName, status: 'failed', postId: null, dispatchError: null, reason: a.reason });
      }
    } catch (err) {
      results.push({
        accountId,
        accountName: null,
        status: 'failed',
        postId: null,
        dispatchError: null,
        reason: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }
  const count = (s: FanOutStatus) => results.filter((r) => r.status === s).length;
  return { results, created: count('created'), skipped: count('skipped'), failed: count('failed') };
}

/**
 * The note above the Post-to-many preview.
 *
 * ⚠️ The preview resolves merge fields against the FIRST selected account, so
 * a clickable {website} (the preview links URLs) checks one account's URL and
 * none of the others'. With more than one account selected the note says so in
 * words, next to the links — otherwise the clickable link quietly implies it
 * has checked every account.
 */
export function multiPreviewNote(firstAccountName: string, selectedCount: number): string {
  if (selectedCount <= 1) return `Preview for ${firstAccountName}.`;
  const others = selectedCount - 1;
  return (
    `Preview for ${firstAccountName} only. Merge fields resolve per account, so any link here ` +
    `is ${firstAccountName}'s — it does not check the other ${others} account${others === 1 ? '' : 's'}.`
  );
}
